import { DEFAULT_THEME_MODE, THEME_STORAGE_KEY } from "./constants";
import { ResolvedTheme, ThemeMode } from "./types";

export const isThemeMode = (value: unknown): value is ThemeMode =>
  value === "light" || value === "dark" || value === "system";

export const getStoredThemePreference = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (isThemeMode(stored)) {
      return stored;
    }
  } catch {
    // Ignore storage access errors
  }
  return DEFAULT_THEME_MODE;
};

export const persistThemePreference = (mode: ThemeMode): void => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // Ignore storage write errors
  }
};

export const getSystemTheme = (): ResolvedTheme => {
  if (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }
  return "light";
};

export const resolveTheme = (mode: ThemeMode): ResolvedTheme => {
  if (mode === "system") {
    return getSystemTheme();
  }
  return mode;
};

export const applyDocumentTheme = (mode: ThemeMode): ResolvedTheme => {
  const resolved = resolveTheme(mode);
  const root = document.documentElement;

  root.dataset.theme = resolved;
  root.classList.toggle("dark", resolved === "dark");
  root.style.colorScheme = resolved;

  return resolved;
};

export const getThemeFromSearchParams = (
  search?: string,
): ThemeMode | null => {
  try {
    const params = new URLSearchParams(
      search ?? (typeof window !== "undefined" ? window.location.search : ""),
    );
    const value = params.get("theme");
    return isThemeMode(value) ? value : null;
  } catch {
    return null;
  }
};

/** Prefer URL theme (from web-app), then localStorage. */
export const getBootstrapThemeMode = (search?: string): ThemeMode => {
  return getThemeFromSearchParams(search) || getStoredThemePreference();
};

export const applyStoredTheme = (): ResolvedTheme => {
  return applyDocumentTheme(getBootstrapThemeMode());
};
